// @ts-check
// Raiz da SPA: rotas públicas (login e cadastro) e a área autenticada com o shell
// (gaveta, card de página, painel lateral, snackbars e atalhos globais).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { navigate, routeState } from './router.js';
import { bootstrapState, isCoordinationFocusedState, loadBootstrap, loadSession, navigationItemsState, sessionState, clearSession } from '../data/metadata-store.js';
import { setUnauthorizedHandler } from '../data/api-client.js';
import { NavigationDrawer } from '../modules/navigation/navigation-drawer.js';
import { SidePanel } from '../modules/side-panel/side-panel.js';
import { sidePanelState } from '../modules/side-panel/side-panel-state.js';
import { useGlobalHotkeys } from '../modules/command-menu/global-hotkeys.js';
import { CommandConfirmation, ShortcutHelpDialog } from '../modules/command-menu/command-overlays.js';
import { RecordIndexPage } from '../modules/object-record/record-index-page.js';
import { RecordShowPage } from '../modules/record-show/record-show-page.js';
import { SettingsExperiencePage, SettingsProfilePage } from '../modules/settings/settings-pages.js';
import { SettingsGeneralPage, SettingsSecurityPage } from '../modules/settings/settings-workspace-pages.js';
import { SettingsObjectNewPage, SettingsObjectsPage } from '../modules/settings/data-model/settings-objects-page.js';
import { SettingsObjectDetailPage } from '../modules/settings/data-model/settings-object-detail-page.js';
import { SettingsFieldDetailPage, SettingsFieldNewPage } from '../modules/settings/data-model/settings-field-page.js';
import { SettingsFunnelPage } from '../modules/settings/data-model/settings-funnel-page.js';
import { SettingsChannelsPage } from '../modules/settings/settings-channels-page.js';
import { SettingsWhatsappTemplatesPage } from '../modules/settings/whatsapp-templates/settings-whatsapp-templates-page.js';
import { MetaSignupDemoPage } from '../modules/settings/meta-signup/meta-signup-demo-page.js';
import { isMockEnabled } from '../data/api-client.js';
import { SettingsMembersPage } from '../modules/settings/settings-members-page.js';
import { SettingsRoleDetailPage, SettingsRolesPage } from '../modules/settings/settings-roles-page.js';
import { InviteAcceptPage } from '../modules/auth/invite-accept-page.js';
import { SettingsAutomationsPage } from '../modules/automations/settings-automations-page.js';
import { AutomationEditorPage } from '../modules/automations/automation-editor-page.js';
import { SettingsNotificationsPage } from '../modules/notifications/settings-notifications-page.js';
import { SettingsDevelopersPage, SettingsWebhookDetailPage } from '../modules/settings/settings-developers-page.js';
import { SettingsApiReferencePage } from '../modules/settings/api-reference-page.js';
import { SettingsCaptureFormDetailPage, SettingsCaptureFormsPage } from '../modules/settings/settings-capture-forms-page.js';
import { SettingsBookingPagesPage } from '../modules/booking/settings-booking-pages-page.js';
import { SettingsBookingPageDetailPage } from '../modules/booking/booking-page-editor.js';
import { ConversationsPage } from '../modules/conversations/conversations-page.js';
import { DashboardPage } from '../modules/dashboards/dashboard-page.js';
import { DrilldownPage } from '../modules/dashboards/drilldown-page.js';
import { CalendarPage } from '../modules/calendar/calendar-page.js';
import { VisitsPage } from '../modules/visits/visits-page.js';
// menu Campanhas da 2.0.0 (spec-2.0.0 seção 4.10): a guarda e as telas comuns são do passo 0; cada área exporta as
// páginas dela num arquivo de entrada
import { CampaignScreenGuard } from '../modules/campaigns/campaign-screen-guard.js';
import { CampaignsLanding, LegacyEmailCampaignRedirect } from '../modules/campaigns/campaigns-landing.js';
import { CampaignTemplatesPage } from '../modules/campaigns/campaign-templates-page.js';
import { EmailCampaignDetailPage, EmailCampaignListPage, EmailTemplateEditorPage } from '../modules/email/email-campaign-routes.js';
import { WhatsappCampaignDetailPage, WhatsappCampaignListPage, WhatsappTemplateEditorPage } from '../modules/whatsapp-campaigns/whatsapp-campaign-routes.js';
import { FlowEditorPage, FlowListPage, FlowStatsPage, FlowTemplatesPage } from '../modules/flows/flow-routes.js';
// módulo Atendimento (3.0.0, contrato 10 de atendimento, seção 24.1): a guarda é da fundação; cada frente entrega as
// páginas dela nos arquivos já importados aqui
import { ServiceHomeRedirect, ServiceScreenGuard } from '../modules/service-desk/service-screen-guard.js';
import { ServiceInboxPage } from '../modules/service-desk/service-inbox-page.js';
import { ConsultationsPage } from '../modules/service-desk/consultations-page.js';
import { OmbudsmanPage } from '../modules/service-desk/ombudsman-page.js';
import { ServiceDeskSettingsPage } from '../modules/service-desk/service-desk-settings-page.js';
import { RetentionPage } from '../modules/retention/retention-page.js';
import { BotsPage } from '../modules/bots/bots-page.js';
import { BotTemplatesPage } from '../modules/bots/bot-templates-page.js';
import { BotEditorPage } from '../modules/bots/bot-editor-page.js';
import { BotStatsPage } from '../modules/bots/bot-stats-page.js';
import { isFocusLayoutRoute } from './layout-mode.js';
import { VisitSummaryPage } from '../modules/visits/visit-summary-page.js';
import { SettingsAccountsPage } from '../modules/calendar/settings-accounts-page.js';
import { SignInPage } from '../modules/auth/sign-in-page.js';
import { SignUpPage } from '../modules/auth/sign-up-page.js';
import { NotFoundPage } from './not-found-page.js';
import { SettingsBillingPage } from '../modules/operations/settings-billing-page.js';
import { SettingsAuditPage } from '../modules/operations/settings-audit-page.js';
import { SettingsPrivacyPage } from '../modules/operations/settings-privacy-page.js';
import { SubscriptionBanner } from '../modules/operations/subscription-banner.js';
import { AdminPage } from '../modules/operations/admin-page.js';
import { SnackbarContainer, showErrorSnackbar } from '../ui/snackbar.js';
import { ManualRunConfirmation } from '../modules/automations/manual-automations.js';
import { Skeleton } from '../ui/feedback.js';
import { MainButton } from '../ui/button.js';
import { t } from '../i18n/index.js';
import { StageTransitionDialog } from '../modules/object-record/stage-transition.js';
import { startLiveUpdates } from '../data/live-updates.js';
import { MobileNavigationBar } from '../modules/navigation/mobile-navigation-bar.js';
import { ReleaseNotesPage } from '../modules/release-notes/release-notes-page.js';
import { ReleaseNotesCard } from '../modules/release-notes/release-notes-card.js';
import { ReleaseNotesAnnouncer } from '../modules/release-notes/release-notes-announcer.js';

setUnauthorizedHandler(() => {
  clearSession();
  const pathname = window.location.pathname;
  if (pathname !== '/welcome' && pathname !== '/sign-up' && !pathname.startsWith('/invite/')) navigate('/welcome', { replace: true });
});

/** Esqueleto do primeiro carregamento (UserOrMetadataLoader, doc 07, 17.2). */
function AppLoader() {
  return html`<div class="app-loader">
    <div class="app-loader-left">
      <${Skeleton} width=${96} height=${16} />
      <div class="app-loader-items">${[0, 1, 2, 3, 4].map((index) => html`<${Skeleton} key=${index} height=${16} />`)}</div>
    </div>
    <div class="page-card-wrapper">
      <section class="page-card">
        <header class="page-header"><div class="page-header-left"><${Skeleton} width=${20} height=${20} /><${Skeleton} width=${120} height=${16} /></div></header>
      </section>
    </div>
  </div>`;
}

function AuthenticatedApp() {
  const [loadError, setLoadError] = useState(/** @type {unknown} */ (null));
  const route = routeState.value;
  useGlobalHotkeys();

  useEffect(() => {
    if (bootstrapState.value && sessionState.value) return;
    let isCancelled = false;
    (async () => {
      try {
        await loadSession();
        await loadBootstrap();
      } catch (error) {
        if (isCancelled) return;
        if (/** @type {any} */ (error)?.statusCode === 401) {
          navigate('/welcome', { replace: true });
          return;
        }
        setLoadError(error);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [sessionState.value === null]);

  // atualização automática das listas e do registro aberto (contrato 08 de navegação, seção 6)
  const isBootstrapReady = Boolean(bootstrapState.value);
  useEffect(() => (isBootstrapReady ? startLiveUpdates() : undefined), [isBootstrapReady]);

  useEffect(() => {
    if (route.name === 'root' && bootstrapState.value) {
      // Coordenação e Visitas começam pela tela Visitas
      const firstItem = isCoordinationFocusedState.value ? { href: '/visits' } : navigationItemsState.value[0];
      navigate(firstItem ? firstItem.href : '/settings/profile', { replace: true });
    }
  }, [route.name, bootstrapState.value]);

  if (loadError) {
    return html`<div class="app-error">
      <div class="app-error-title">${t('errors.loadTitle')}</div>
      <div class="app-error-subtitle">${t('errors.loadSubtitle')}</div>
      <div class="app-error-action"><${MainButton} title=${t('errors.reload')} variant="secondary" onClick=${() => window.location.reload()} /></div>
    </div>`;
  }
  if (!bootstrapState.value) return html`<${AppLoader} />`;

  let page;
  switch (route.name) {
    case 'recordIndex':
      page = html`<${RecordIndexPage} key=${route.params.objectNamePlural} objectNamePlural=${route.params.objectNamePlural} />`;
      break;
    case 'recordShow':
      page = html`<${RecordShowPage} key=${`${route.params.objectNameSingular}-${route.params.recordId}`} objectNameSingular=${route.params.objectNameSingular} recordId=${route.params.recordId} />`;
      break;
    case 'conversations':
      page = html`<${ConversationsPage} threadId=${route.params.threadId ?? null} />`;
      break;
    case 'calendar':
      page = html`<${CalendarPage} />`;
      break;
    case 'campaignsHome':
      page = html`<${CampaignScreenGuard} screenKey="home"><${CampaignsLanding} /><//>`;
      break;
    case 'campaignTemplates':
      page = html`<${CampaignScreenGuard} screenKey="templates"><${CampaignTemplatesPage} /><//>`;
      break;
    case 'campaignEmailTemplateNew':
    case 'campaignEmailTemplate':
      page = html`<${CampaignScreenGuard} screenKey="templates">
        <${EmailTemplateEditorPage} key=${route.params.templateId ?? 'new'} templateId=${route.params.templateId ?? null} />
      <//>`;
      break;
    case 'campaignWhatsappTemplateNew':
    case 'campaignWhatsappTemplate':
      page = html`<${CampaignScreenGuard} screenKey="templates">
        <${WhatsappTemplateEditorPage} key=${route.params.templateId ?? 'new'} templateId=${route.params.templateId ?? null} />
      <//>`;
      break;
    case 'emailCampaigns':
      page = html`<${CampaignScreenGuard} screenKey="email"><${EmailCampaignListPage} /><//>`;
      break;
    case 'emailCampaignDetail':
      page = html`<${CampaignScreenGuard} screenKey="email"><${EmailCampaignDetailPage} key=${route.params.campaignId} campaignId=${route.params.campaignId} /><//>`;
      break;
    case 'legacyEmailCampaign':
      page = html`<${LegacyEmailCampaignRedirect} key=${route.params.campaignId} campaignId=${route.params.campaignId} />`;
      break;
    case 'whatsappCampaigns':
      page = html`<${CampaignScreenGuard} screenKey="whatsapp"><${WhatsappCampaignListPage} /><//>`;
      break;
    case 'whatsappCampaignDetail':
      page = html`<${CampaignScreenGuard} screenKey="whatsapp"><${WhatsappCampaignDetailPage} key=${route.params.campaignId} campaignId=${route.params.campaignId} /><//>`;
      break;
    case 'automationFlows':
      page = html`<${CampaignScreenGuard} screenKey="flows"><${FlowListPage} /><//>`;
      break;
    case 'automationFlowTemplates':
      page = html`<${CampaignScreenGuard} screenKey="flows"><${FlowTemplatesPage} /><//>`;
      break;
    case 'automationFlowStats':
      page = html`<${CampaignScreenGuard} screenKey="flows"><${FlowStatsPage} key=${route.params.flowId} flowId=${route.params.flowId} /><//>`;
      break;
    case 'automationFlowDetail':
      page = html`<${CampaignScreenGuard} screenKey="flows"><${FlowEditorPage} key=${route.params.flowId} flowId=${route.params.flowId} /><//>`;
      break;
    case 'serviceHome':
      page = html`<${ServiceScreenGuard} screenKey="home"><${ServiceHomeRedirect} /><//>`;
      break;
    case 'serviceInbox':
      page = html`<${ServiceScreenGuard} screenKey="inbox"><${ServiceInboxPage} ticketId=${route.params.ticketId ?? null} threadId=${null} /><//>`;
      break;
    case 'serviceInboxThread':
      page = html`<${ServiceScreenGuard} screenKey="inbox"><${ServiceInboxPage} ticketId=${null} threadId=${route.params.threadId} /><//>`;
      break;
    case 'serviceConsultations':
      page = html`<${ServiceScreenGuard} screenKey="consultations"><${ConsultationsPage} ticketId=${route.params.ticketId ?? null} /><//>`;
      break;
    case 'serviceOmbudsman':
      page = html`<${ServiceScreenGuard} screenKey="ombudsman"><${OmbudsmanPage} /><//>`;
      break;
    case 'serviceRetention':
      page = html`<${ServiceScreenGuard} screenKey="retention"><${RetentionPage} /><//>`;
      break;
    case 'serviceBots':
      page = html`<${ServiceScreenGuard} screenKey="bots"><${BotsPage} /><//>`;
      break;
    case 'serviceBotTemplates':
      page = html`<${ServiceScreenGuard} screenKey="bots"><${BotTemplatesPage} /><//>`;
      break;
    case 'serviceBotDetail':
      page = html`<${ServiceScreenGuard} screenKey="bots"><${BotEditorPage} key=${route.params.botId} botId=${route.params.botId} /><//>`;
      break;
    case 'serviceBotStats':
      page = html`<${ServiceScreenGuard} screenKey="bots"><${BotStatsPage} key=${route.params.botId} botId=${route.params.botId} /><//>`;
      break;
    case 'serviceDeskSettings':
      page = html`<${ServiceScreenGuard} screenKey="settings"><${ServiceDeskSettingsPage} /><//>`;
      break;
    case 'visits':
      page = html`<${VisitsPage} />`;
      break;
    case 'visitSummary':
      page = html`<${VisitSummaryPage} key=${route.params.enrollmentOpportunityId} enrollmentOpportunityId=${route.params.enrollmentOpportunityId} />`;
      break;
    case 'settingsAccounts':
      page = html`<${SettingsAccountsPage} />`;
      break;
    case 'releaseNotes':
      page = html`<${ReleaseNotesPage} />`;
      break;
    case 'dashboards':
      page = html`<${DashboardPage} dashboardId=${route.params.dashboardId ?? null} />`;
      break;
    case 'dashboardDrilldown':
      page = html`<${DrilldownPage} />`;
      break;
    case 'settingsChannels':
      page = html`<${SettingsChannelsPage} />`;
      break;
    case 'settingsWhatsappTemplates':
      page = html`<${SettingsWhatsappTemplatesPage} />`;
      break;
    case 'settingsMembers':
      page = html`<${SettingsMembersPage} />`;
      break;
    case 'settingsRoles':
      page = html`<${SettingsRolesPage} />`;
      break;
    case 'settingsRoleDetail':
      page = html`<${SettingsRoleDetailPage} key=${route.params.roleId} roleId=${route.params.roleId} />`;
      break;
    case 'settingsAutomations':
      page = html`<${SettingsAutomationsPage} />`;
      break;
    case 'settingsAutomationDetail':
      page = html`<${AutomationEditorPage} key=${route.params.automationId} automationId=${route.params.automationId} />`;
      break;
    case 'settingsNotifications':
      page = html`<${SettingsNotificationsPage} />`;
      break;
    case 'settingsDevelopers':
      page = html`<${SettingsDevelopersPage} />`;
      break;
    case 'settingsApiReference':
      page = html`<${SettingsApiReferencePage} />`;
      break;
    case 'settingsWebhookDetail':
      page = html`<${SettingsWebhookDetailPage} key=${route.params.webhookId} webhookId=${route.params.webhookId} />`;
      break;
    case 'settingsBookingPages':
      page = html`<${SettingsBookingPagesPage} />`;
      break;
    case 'settingsBookingPageDetail':
      page = html`<${SettingsBookingPageDetailPage} key=${route.params.bookingPageId} bookingPageId=${route.params.bookingPageId} />`;
      break;
    case 'settingsCaptureForms':
      page = html`<${SettingsCaptureFormsPage} />`;
      break;
    case 'settingsCaptureFormDetail':
      page = html`<${SettingsCaptureFormDetailPage} key=${route.params.formId} formId=${route.params.formId} />`;
      break;
    case 'settingsBilling':
      page = html`<${SettingsBillingPage} />`;
      break;
    case 'settingsAuditLog':
      page = html`<${SettingsAuditPage} />`;
      break;
    case 'settingsPrivacy':
      page = html`<${SettingsPrivacyPage} />`;
      break;
    case 'settingsProfile':
      page = html`<${SettingsProfilePage} />`;
      break;
    case 'settingsExperience':
      page = html`<${SettingsExperiencePage} />`;
      break;
    case 'settingsGeneral':
      page = html`<${SettingsGeneralPage} />`;
      break;
    case 'settingsSecurity':
      page = html`<${SettingsSecurityPage} />`;
      break;
    case 'settingsObjects':
      page = html`<${SettingsObjectsPage} />`;
      break;
    case 'settingsObjectDetail':
      page = html`<${SettingsObjectDetailPage} key=${route.params.objectNamePlural} objectNamePlural=${route.params.objectNamePlural} />`;
      break;
    case 'settingsObjectNew':
      page = html`<${SettingsObjectNewPage} />`;
      break;
    case 'settingsFieldNew':
      page = html`<${SettingsFieldNewPage} key=${route.params.objectNamePlural} objectNamePlural=${route.params.objectNamePlural} />`;
      break;
    case 'settingsFieldDetail':
      page = html`<${SettingsFieldDetailPage} objectNamePlural=${route.params.objectNamePlural} fieldName=${route.params.fieldName} />`;
      break;
    case 'settingsFunnel':
      page = html`<${SettingsFunnelPage} />`;
      break;
    case 'root':
      page = null;
      break;
    default:
      page = html`<${NotFoundPage} />`;
  }

  // modo foco (editor de fluxo, 2.0.0): sem o menu lateral nem a barra flutuante; os diálogos globais continuam. O cartão
  // das novidades também sai, porque no celular cobria o "Salvar alterações" do editor; ele volta fora do modo foco
  const isFocusLayout = isFocusLayoutRoute(route.name);
  return html`<div class=${isFocusLayout ? 'app-layout app-layout-focus' : 'app-layout'}>
    <${SubscriptionBanner} />
    <div class="page-container">
      ${isFocusLayout ? null : html`<${NavigationDrawer} />`}
      <main class="main-container" data-side-panel-open=${sidePanelState.value ? 'true' : undefined}>
        ${page}
        <${SidePanel} />
      </main>
    </div>
    ${isFocusLayout ? null : html`<${MobileNavigationBar} />`}
    ${isFocusLayout ? null : html`<${ReleaseNotesCard} />`}
    <${ReleaseNotesAnnouncer} />
    <${CommandConfirmation} />
    <${ShortcutHelpDialog} />
  </div>`;
}

export function App() {
  const route = routeState.value;
  let content;
  if (route.name === 'signIn') {
    content = html`<${SignInPage}
      onSignedIn=${async () => {
        try {
          await loadSession();
          await loadBootstrap();
          navigate('/', { replace: true });
        } catch (error) {
          showErrorSnackbar(error);
        }
      }}
    />`;
  } else if (route.name === 'signUp') {
    content = html`<${SignUpPage} />`;
  } else if (route.name === 'inviteAccept') {
    content = html`<${InviteAcceptPage}
      token=${route.params.token}
      onAccepted=${async () => {
        try {
          await loadSession();
          await loadBootstrap();
          navigate('/', { replace: true });
        } catch (error) {
          showErrorSnackbar(error);
        }
      }}
    />`;
  } else if (route.name === 'metaSignupDemo' && isMockEnabled()) {
    // popup do "Conectar com a Meta" no modo demonstração: fora da área logada, como a página do portal
    content = html`<${MetaSignupDemoPage} />`;
  } else if (route.name === 'admin') {
    // painel interno de suporte: só no portal, com sessão própria
    content = html`<${AdminPage} section=${route.params.section} id=${route.params.id} />`;
  } else {
    content = html`<${AuthenticatedApp} />`;
  }
  return html`${content}<${SnackbarContainer} /><${StageTransitionDialog} /><${ManualRunConfirmation} />`;
}
